// [목적] 인라인 에러 상태 컴포넌트 — 실패 안내 문구 + "다시 시도" 버튼(role=alert, 접근성 포커스 가능)
// [이유] G1-2: ProfilePage(usePublicProfile 실패 시 스켈레톤 무한 유지)/MyFoliosPage(목록 error 미처리)/
//        ExplorePage(공식 컬렉션 실패 시 조용히 숨김) 3곳이 각자 다른(혹은 부재한) 에러 UI 를 쓰던 것을
//        하나의 공용 컴포넌트로 통일한다. 전체화면 ErrorBoundary(class, throwOnError 전용)와 달리
//        throwOnError:false 쿼리(usePublicProfile 등)가 반환하는 error 를 페이지가 직접 렌더할 때 쓴다.
// [사이드 이팩트] 없음(순수 표시 컴포넌트) — onRetry 콜백만 호출(보통 refetch()).
// [수정 시 고려사항] role="alert" 로 스크린리더 즉시 announce — 폴링/반복 렌더에서 남용하지 말 것
//                   (마운트 시 1회성 실패 안내 용도). 재시도 버튼은 aria-label 없이 텍스트 자체로 충분(짧고 명확).

import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

const c = tokens.color;
const L = 'light' as const;
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const BRAND = c.brand[500][L];

export interface InlineErrorStateProps {
  /** 실패 안내 문구 — 미지정 시 범용 문구 사용 */
  message?: string;
  /** 재시도 버튼 클릭 핸들러 — 보통 TanStack Query 의 refetch() */
  onRetry: () => void;
  /** 재시도 버튼 라벨 — 기본 "다시 시도" */
  retryLabel?: string;
}

/**
 * [목적] 목록/상세 조회 실패 시 공통 인라인 에러 UI — 문구 + 재시도 버튼
 * [이유] G1-2: 여러 페이지가 각자 스켈레톤 무한 표시 또는 조용한 숨김으로 실패를 감추던 문제를 통일된
 *        가시적 UI(+ 복구 경로)로 대체.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 페이지별 레이아웃(그리드/리스트/헤더) 안에서 자유롭게 배치 가능하도록 폭 제약을 두지 않음.
 */
export function InlineErrorState({
  message = '데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  onRetry,
  retryLabel = '다시 시도',
}: InlineErrorStateProps): JSX.Element {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: `${tokens.spacing[2]}px`,
        textAlign: 'center',
        p: `${tokens.spacing[8]}px ${tokens.spacing[4]}px`,
      }}
    >
      <Typography sx={{ fontSize: 13, color: INK_MID }}>{message}</Typography>
      <ButtonBase
        onClick={onRetry}
        sx={{
          borderRadius: `${tokens.radius.xl}px`,
          px: `${tokens.spacing[3]}px`,
          py: `${tokens.spacing[1]}px`,
          fontSize: 12.5,
          fontWeight: 600,
          color: BRAND,
          border: `1px solid ${BORDER}`,
          '&:focus-visible': {
            outline: `2px solid ${BRAND}`,
            outlineOffset: `${tokens.spacing[1]}px`,
          },
        }}
      >
        {retryLabel}
      </ButtonBase>
    </Box>
  );
}
